import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
const BE = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BE}${u}`);

/**
 * Full-screen lightbox for gallery photos.
 * Props:
 *   items      — array of { id?, image_url, title, tag }
 *   index      — current photo index, or null to hide
 *   onClose    — () => void
 *   onIndex    — (nextIndex) => void
 */
export const Lightbox = ({ items, index, onClose, onIndex }) => {
  const open = index !== null && index !== undefined && items?.[index];

  const prev = useCallback(() => {
    if (!items?.length) return;
    onIndex((index - 1 + items.length) % items.length);
  }, [index, items, onIndex]);

  const next = useCallback(() => {
    if (!items?.length) return;
    onIndex((index + 1) % items.length);
  }, [index, items, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, prev, next, onClose]);

  if (!open) return null;
  const it = items[index];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      data-testid="lightbox"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 lg:px-10 py-4 border-b border-white/10 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 pr-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5722]" data-testid="lightbox-tag">{it.tag}</div>
          <div className="font-display text-xl md:text-2xl tracking-tight text-white truncate" data-testid="lightbox-title">{it.title}</div>
          {it.description && (
            <p className="text-sm text-zinc-300 mt-1 max-w-2xl leading-relaxed" data-testid="lightbox-description">{it.description}</p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs uppercase tracking-widest text-zinc-500" data-testid="lightbox-count">{index + 1} / {items.length}</span>
          <button
            onClick={onClose}
            className="w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#FF5722] hover:text-[#FF5722] transition-colors"
            aria-label="Close (Esc)"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 relative flex items-center justify-center min-h-0" onClick={(e) => e.stopPropagation()}>
        <img
          src={resolveUrl(it.image_url)}
          alt={it.title}
          className="max-h-full max-w-full object-contain px-16"
          data-testid="lightbox-image"
        />

        {items.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 border border-white/20 bg-black/50 hover:bg-[#FF5722] hover:border-[#FF5722] transition-colors flex items-center justify-center text-white"
              aria-label="Previous photo"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={next}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 border border-white/20 bg-black/50 hover:bg-[#FF5722] hover:border-[#FF5722] transition-colors flex items-center justify-center text-white"
              aria-label="Next photo"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-6 lg:px-10 py-3 border-t border-white/10 text-center text-[10px] uppercase tracking-widest text-zinc-500 shrink-0" onClick={(e) => e.stopPropagation()}>
        <kbd className="text-white">Esc</kbd> Close · <kbd className="text-white">←</kbd> <kbd className="text-white">→</kbd> Navigate · Click outside to close
      </div>
    </div>
  );
};
